import {
  GeneralSaleTransactionType,
  PayinType,
  PayoutType,
  TimeLength,
  TrustStatus
} from '@sollapay/enums';
import {
  FieldMoneyInput,
  FieldMultiSelect,
  FieldSelect,
  FieldTextarea,
  FormSectionHeading
} from '@sollapay/ui/components';
import {Controller, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {FormShell} from '@/components';
import {generateTrustVolumeNis, withDebugFieldChange} from '@/features/debug-test-data';
import {useShowErrors} from '@/features/trust-accounts/activation/hooks';
import {useCurrentTrustAccount} from '@/features/trust-accounts/providers';

import {PAYOUT_TYPE_LABEL_KEYS} from '../constants';
import {useTrustScopeForm} from '../hooks';

import type {FormStateSnapshot} from '@/hooks';
import type {TrustScopeDetailsDTO} from '@sollapay/types';
import type {FC} from 'react';

interface TrustScopeStepFormProps {
  trustAccountId: string;
  scope: TrustScopeDetailsDTO | null | undefined;
  isLoading: boolean;
  isError: boolean;
  onFormStateChange?: (state: FormStateSnapshot) => void;
}

export const TrustScopeStepForm: FC<TrustScopeStepFormProps> = ({
  trustAccountId,
  scope,
  isLoading,
  isError,
  onFormStateChange
}) => {
  const {t} = useTranslation(['trustAccounts', 'common']);
  const showErrors = useShowErrors();
  const {trustStatus} = useCurrentTrustAccount();
  const {form, handleSave, saveError, fieldErrors, requiresTransactionType, availablePayoutTypes} =
    useTrustScopeForm({
      trustAccountId,
      scope,
      isLoading,
      isError,
      onFormStateChange
    });
  const {control, register, setValue} = form;

  const payoutTypes = useWatch({control, name: 'payoutTypes'}) ?? [];
  const payinTypes = useWatch({control, name: 'payinTypes'}) ?? [];

  const hasOtherPayout = payoutTypes.includes(PayoutType.OTHER);
  const hasOtherPayin = payinTypes.includes(PayinType.OTHER);

  const timeLengthOptions = [
    {
      value: TimeLength.UP_TO_1W,
      label: t('activation.packages.trustDetails.form.options.timeLength.up_to_1w')
    },
    {
      value: TimeLength.W1_M1,
      label: t('activation.packages.trustDetails.form.options.timeLength.w1_m1')
    },
    {
      value: TimeLength.M1_M3,
      label: t('activation.packages.trustDetails.form.options.timeLength.m1_m3')
    },
    {
      value: TimeLength.GT_3M,
      label: t('activation.packages.trustDetails.form.options.timeLength.gt_3m')
    }
  ];

  const payoutTypeOptions = availablePayoutTypes.map(value => ({
    value,
    label: t(
      `activation.packages.trustDetails.form.options.payoutTypes.${PAYOUT_TYPE_LABEL_KEYS[value]}`
    )
  }));

  const payinTypeOptions = [
    {
      value: PayinType.BUYERS,
      label: t('activation.packages.trustDetails.form.options.payinTypes.buyers')
    },
    {
      value: PayinType.OTHER,
      label: t('activation.packages.trustDetails.form.options.payinTypes.other')
    }
  ];

  const transactionTypeOptions = [
    {
      value: GeneralSaleTransactionType.SECOND_HAND_SALE,
      label: t('activation.packages.trustDetails.form.options.transactionTypes.secondHandSale')
    },
    {
      value: GeneralSaleTransactionType.OPTION_ASSIGNMENT,
      label: t('activation.packages.trustDetails.form.options.transactionTypes.optionAssignment')
    }
  ];

  const fe = (key: string) => (showErrors && fieldErrors[key] ? t(fieldErrors[key]) : undefined);

  return (
    <FormShell
      id="trust-scope-form"
      className="flex flex-col gap-6"
      onSubmit={handleSave}
      saveError={saveError}
    >
      <FormSectionHeading
        title={t('activation.packages.trustDetails.trustScope.title')}
        description={t('activation.packages.trustDetails.trustScope.description')}
      />
      <div className="grid grid-cols-2 gap-6">
        {requiresTransactionType && (
          <Controller
            name="transactionType"
            control={control}
            render={({field}) => (
              <FieldSelect
                fieldClassName="col-span-2"
                label={t('activation.packages.trustDetails.form.fields.transactionType')}
                required
                placeholder={t(
                  'activation.packages.trustDetails.form.fields.transactionTypePlaceholder'
                )}
                value={field.value}
                onValueChange={value => {
                  field.onChange(value);
                  // Each deal type offers a different payout-destination list — a selection made
                  // under the old type could no longer be valid, so start the picker over.
                  setValue('payoutTypes', [], {shouldDirty: true});
                  setValue('payoutOtherText', '', {shouldDirty: true});
                }}
                options={transactionTypeOptions}
                // Fixed once the account is submitted — it decides what every package requires.
                isLocked={trustStatus !== TrustStatus.SETUP_IN_PROGRESS}
                error={fe('transactionType')}
              />
            )}
          />
        )}

        <Controller
          name="timeLength"
          control={control}
          render={({field}) => (
            <FieldSelect
              label={t('activation.packages.trustDetails.form.fields.timeLength')}
              required
              placeholder={t('activation.packages.trustDetails.form.fields.timeLengthPlaceholder')}
              value={field.value as TimeLength}
              onValueChange={(value: string) => field.onChange(value as TimeLength)}
              options={timeLengthOptions}
              error={fe('timeLength')}
            />
          )}
        />

        <Controller
          name="volume"
          control={control}
          render={({field}) => (
            <FieldMoneyInput
              name={field.name}
              label={t('activation.packages.trustDetails.form.fields.volume')}
              required
              placeholder={t('activation.packages.trustDetails.form.fields.volumePlaceholder')}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fe('volume')}
              cornerBadge={withDebugFieldChange(undefined, field.onChange, generateTrustVolumeNis)}
            />
          )}
        />

        <div className="col-span-2 grid grid-cols-2 gap-6">
          <div className="flex flex-col gap-6">
            <Controller
              name="payinTypes"
              control={control}
              render={({field}) => (
                <FieldMultiSelect
                  label={t('activation.packages.trustDetails.form.fields.payinTypes')}
                  required
                  placeholder={t(
                    'activation.packages.trustDetails.form.fields.payinTypesPlaceholder'
                  )}
                  value={field.value}
                  onValueChange={(values: string[]) => field.onChange(values as PayinType[])}
                  options={payinTypeOptions}
                  error={fe('payinTypes')}
                />
              )}
            />

            {hasOtherPayin && (
              <FieldTextarea
                placeholder={t(
                  'activation.packages.trustDetails.form.fields.payinOtherTextPlaceholder'
                )}
                error={fe('payinOtherText')}
                {...register('payinOtherText')}
              />
            )}
          </div>

          <div className="flex flex-col gap-6">
            <Controller
              name="payoutTypes"
              control={control}
              render={({field}) => (
                <FieldMultiSelect
                  label={t('activation.packages.trustDetails.form.fields.payoutTypes')}
                  required
                  placeholder={t(
                    'activation.packages.trustDetails.form.fields.payoutTypesPlaceholder'
                  )}
                  value={field.value}
                  onValueChange={(values: string[]) => field.onChange(values as PayoutType[])}
                  options={payoutTypeOptions}
                  error={fe('payoutTypes')}
                />
              )}
            />

            {hasOtherPayout && (
              <FieldTextarea
                placeholder={t(
                  'activation.packages.trustDetails.form.fields.payoutOtherTextPlaceholder'
                )}
                error={fe('payoutOtherText')}
                {...register('payoutOtherText')}
              />
            )}
          </div>
        </div>
      </div>
    </FormShell>
  );
};
